import { useCallback, useEffect, useRef, useState } from "react";
import {
  getAttioStatus,
  getCalendarStatus,
  getGmailStatus,
  isGmailReadyForPerformer,
} from "../../businessModeService";
import {
  startPerformerAttioOAuthFlow,
  startPerformerCalendarOAuthFlow,
  startPerformerGmailOAuthFlow,
} from "../performerGmailOAuth";
import config from "../../../core/config";
import {
  hasStoredGateChoice,
  markGateChoiceMade,
  resolveRelevantConnectors,
} from "./connectorRunGate";
import ConnectorRunGateModal from "./ConnectorRunGateModal";

/** True when a connector's live status says it's already connected. */
const isConnectorConnected = async (connectorId) => {
  if (connectorId === "gmail") {
    const status = await getGmailStatus({ verify: "performer" });
    return isGmailReadyForPerformer(status);
  }
  if (connectorId === "calendar") {
    const status = await getCalendarStatus();
    return status?.connected === true || status?.calendar_authorized === true;
  }
  if (connectorId === "attio") {
    const status = await getAttioStatus();
    return status?.connected === true;
  }
  return false;
};

/** Fetch an OAuth auth_url for a Gmail/Calendar-style connect (Attio is self-contained). */
const fetchAuthUrl = async (path) => {
  const response = await fetch(`${config.BACKEND_URL}${path}`, {
    method: "GET",
    credentials: "include",
  });
  const payload = await response.json().catch(() => ({}));
  return payload?.auth_url || "";
};

/**
 * Pre-run connect/skip gate for Gmail, Calendar, and Attio.
 *
 * `guardedRun(runFn, opts, { aiAssistType })` replaces a direct `runFn(opts)`
 * call. If every connector relevant to `aiAssistType` already has a stored
 * connect-or-skip choice, it calls `runFn(opts)` immediately with no modal.
 * Otherwise it checks live status for the undecided ones (auto-marking any
 * that are already connected) and, if any are still undecided, shows a modal
 * before running — Connect wires up that integration, Skip proceeds as-is.
 * Render the returned `gateModal` as a sibling in the caller's JSX.
 *
 * @example
 * const { guardedRun, gateModal } = useConnectorRunGate();
 * guardedRun(handleRetry, opts, { aiAssistType: item.ai_assist_type });
 */
export function useConnectorRunGate() {
  const [pending, setPending] = useState([]);
  const [loadingConnectorId, setLoadingConnectorId] = useState(null);
  const pendingRef = useRef([]);
  const pendingRunRef = useRef(null);
  const stopOAuthRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopOAuthRef.current?.();
    };
  }, []);

  const setPendingList = useCallback((next) => {
    pendingRef.current = next;
    setPending(next);
  }, []);

  const finishRun = useCallback(() => {
    const stored = pendingRunRef.current;
    pendingRunRef.current = null;
    setPendingList([]);
    if (stored) stored.runFn(stored.opts);
  }, [setPendingList]);

  const resolveConnector = useCallback(
    (connectorId) => {
      markGateChoiceMade(connectorId);
      const next = pendingRef.current.filter((id) => id !== connectorId);
      if (next.length === 0) {
        finishRun();
        return;
      }
      setPendingList(next);
    },
    [finishRun, setPendingList]
  );

  const handleSkip = useCallback(
    (connectorId) => {
      resolveConnector(connectorId);
    },
    [resolveConnector]
  );

  const handleConnect = useCallback(
    (connectorId) => {
      if (loadingConnectorId) return;
      setLoadingConnectorId(connectorId);
      stopOAuthRef.current?.();

      const onReady = () => {
        if (!mountedRef.current) return;
        setLoadingConnectorId(null);
        resolveConnector(connectorId);
      };
      const onPollEnd = () => {
        if (!mountedRef.current) return;
        setLoadingConnectorId(null);
      };

      if (connectorId === "attio") {
        stopOAuthRef.current = startPerformerAttioOAuthFlow(onReady, onPollEnd);
        return;
      }

      const authPath =
        connectorId === "gmail"
          ? "/externalauth/gmail/auth_url?purpose=performer"
          : "/externalauth/google_calendar/auth_url";
      void fetchAuthUrl(authPath).then((authUrl) => {
        if (!mountedRef.current) return;
        if (!authUrl) {
          setLoadingConnectorId(null);
          return;
        }
        stopOAuthRef.current =
          connectorId === "gmail"
            ? startPerformerGmailOAuthFlow(authUrl, onReady, onPollEnd)
            : startPerformerCalendarOAuthFlow(authUrl, onReady);
      });
    },
    [loadingConnectorId, resolveConnector]
  );

  const handleDismiss = useCallback(() => {
    stopOAuthRef.current?.();
    pendingRunRef.current = null;
    setLoadingConnectorId(null);
    setPendingList([]);
  }, [setPendingList]);

  const guardedRun = useCallback((runFn, opts, { aiAssistType } = {}) => {
    const relevant = resolveRelevantConnectors(aiAssistType);
    const undecided = relevant.filter((id) => !hasStoredGateChoice(id));
    if (undecided.length === 0) {
      runFn(opts);
      return;
    }

    pendingRunRef.current = { runFn, opts };

    void Promise.all(
      undecided.map(async (id) => {
        try {
          const connected = await isConnectorConnected(id);
          if (connected) markGateChoiceMade(id);
          return { id, connected };
        } catch {
          // Fail open: drop the row rather than block the run on a status-check error.
          return { id, connected: true };
        }
      })
    ).then((results) => {
      if (!mountedRef.current) return;
      const stillPending = results.filter((r) => !r.connected).map((r) => r.id);
      if (stillPending.length === 0) {
        finishRun();
        return;
      }
      setPendingList(stillPending);
    });
  }, [finishRun, setPendingList]);

  const gateModal =
    pending.length > 0 ? (
      <ConnectorRunGateModal
        pendingConnectors={pending}
        loadingConnectorId={loadingConnectorId}
        onConnect={handleConnect}
        onSkip={handleSkip}
        onDismiss={handleDismiss}
      />
    ) : null;

  return { guardedRun, gateModal };
}
